import { LaunchState } from "../components/LaunchState";
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { ShoppingBag, ExternalLink, X, BookmarkPlus, SlidersHorizontal, Check, ArrowDown, TrendingUp } from "lucide-react";
import api, { apiError } from "../lib/apiClient";
import DealCard from "../components/DealCard";
import { DemoCollectionsGrid } from "../components/DemoCollectionsGrid";
import FindMeTabs from "../components/FindMeTabs";
import { Editable } from "../components/Editable";
import { AFFILIATE_DISCLOSURE as DISCLOSURE } from "../lib/siteConfig";
import { useAuth } from "../context/AuthContext";
import { FamilyFilterPanel, Pill, WHOM, OCCASIONS, SEASONS, SLEEPWEAR, SLEEPWEAR_RULE, DEFAULT_CRITERIA } from "../components/FamilyFilterPanel";

const isGirl = (c) => (c.type || "").includes("girl");
const isBoy = (c) => (c.type || "").includes("boy");
// Children shown as selectable chips depend on the "who" selection.
// Selected-family total / budget / exact overage under a result card (same numbers the watcher uses).
function BudgetLine({ bs, id }) {
  if (!bs) return null;
  const over = bs.within === false;
  return (
    <div className={`rounded-2xl border px-4 py-2.5 text-sm ${over ? "border-brand/30 bg-blush/40" : "border-success/30 bg-success/5"}`} data-testid={`budget-line-${id}`}>
      <p className="font-bold text-ink">Your family: ${Number(bs.selected_total).toFixed(2)} <span className="text-ink-muted font-semibold">· budget ${Number(bs.budget_limit ?? bs.budget_amount).toFixed(2)}{bs.budget_type === "per_child" ? " (per child × selected)" : ""}</span></p>
      <p className={`text-xs mt-0.5 ${over ? "text-brand font-bold" : "text-success font-bold"}`} data-testid={`budget-overage-${id}`}>{over ? `$${Number(bs.overage).toFixed(2)} over your budget` : "Within your budget"}{bs.extras_total > 0 ? ` · includes $${Number(bs.extras_total).toFixed(2)} selected accessories` : ""}</p>
    </div>
  );
}

const filterByWhom = (kids, whom) => {
  if (whom === "boys") return kids.filter(isBoy);
  if (whom === "girls") return kids.filter(isGirl);
  return kids;
};
// Shared filter state ⇄ URL (deep links): occasion, season, sleepwear, audience.
const readUrl = () => {
  const p = new URLSearchParams(window.location.search);
  const occ = OCCASIONS.some((o) => o.v === p.get("occasion")) ? p.get("occasion") : null;
  const season = SEASONS.some((s) => s.v === (p.get("season") || "").split(",")[0]) ? p.get("season").split(",")[0] : null;
  const sleep = SLEEPWEAR.some((s) => s.v === p.get("sleepwear")) ? p.get("sleepwear") : null;
  const aud = p.get("audience");
  return { occasion: occ, season, sleepwear_style: sleep, whom: aud === "boys" || aud === "girls" ? aud : null };
};
const audienceOf = (whom) => (whom === "boys" || whom === "girls" ? whom : "all");

export default function FamilyDeals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [family, setFamily] = useState(null);
  const [guestFamily, setGuestFamily] = useState(() => readGuestFamily());
  const saveGuestFamily = (g) => { setGuestFamily(g); writeGuestFamily(g); };
  const fam = user ? family : guestFamily; // guests browse with device-only children; a signed-in family is never overwritten by guest entries
  const [famLoaded, setFamLoaded] = useState(false);
  const [criteria, setCriteria] = useState(() => { const u = readUrl(); return { ...DEFAULT_CRITERIA, whom: location.state?.whom || u.whom || "kids_only", occasion: u.occasion || "pajamas", season: u.season || "all_seasons", sleepwear_style: u.sleepwear_style || "" }; });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingRun, setPendingRun] = useState(false);
  const [linkModal, setLinkModal] = useState(null);
  const [fits, setFits] = useState({});
  const [saveOpen, setSaveOpen] = useState(false);
  const [dur, setDur] = useState({ mode: "30", endDate: "" });
  const [openId, setOpenId] = useState(null);
  const [restoreNote, setRestoreNote] = useState(null); // saved-search children that no longer exist in the family
  const [catalogEmpty, setCatalogEmpty] = useState(null); // true once /collections returns nothing (prelaunch)
  const [demoCount, setDemoCount] = useState(0); // demo collections shown in the shopping layout replace the prelaunch notices

  useEffect(() => {
    setFits({});
  }, [user]);

  useEffect(() => {
    if (user) return; // guest: selectable children follow the device-only list
    setCriteria((cr) => ({ ...cr, child_ids: cr.whom === "pick" ? cr.child_ids.filter((id) => (guestFamily.children || []).some((c) => c.id === id)) : filterByWhom(guestFamily.children || [], cr.whom).map((c) => c.id) }));
  }, [user, guestFamily]);

  useEffect(() => {
    if (user) api.get("/family").then(({ data }) => {
      setFamily(data);
      if (data && data.children) setCriteria((cr) => ({ ...cr, child_ids: cr.whom === "pick" ? cr.child_ids.filter((id) => data.children.some((c) => c.id === id)) : filterByWhom(data.children, cr.whom).map((c) => c.id) }));
    }).catch(() => {}).finally(() => setFamLoaded(true));
  }, [user]);

  // Switch the "who" group and reset the selectable children to match it.
  const selectWhom = (v) => {
    const kids = (fam && fam.children) || [];
    const ids = v === "pick" ? [] : filterByWhom(kids, v).map((c) => c.id);
    setCriteria((cr) => ({ ...cr, whom: v, child_ids: ids }));
  };

  // Zero-member audience (#8): explain and block Find/Save until the user changes it or adds a child.
  const eligibleKids = (fam && fam.children) ? filterByWhom(fam.children, criteria.whom) : [];
  const audienceZero = !!(fam && (fam.children || []).length > 0 &&
    (criteria.whom === "pick" ? criteria.child_ids.length === 0 : eligibleKids.length === 0));

  // One shared filter state drives the finder AND the Featured Collections list; URL mirrors it for deep links/Back.
  const skipPush = React.useRef(true);
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (criteria.occasion) url.searchParams.set("occasion", criteria.occasion);
      if (criteria.season && criteria.season !== "all_seasons") url.searchParams.set("season", criteria.season); else url.searchParams.delete("season");
      if (criteria.occasion === "pajamas" && criteria.sleepwear_style) url.searchParams.set("sleepwear", criteria.sleepwear_style); else url.searchParams.delete("sleepwear");
      if (audienceOf(criteria.whom) !== "all") url.searchParams.set("audience", audienceOf(criteria.whom)); else url.searchParams.delete("audience");
      const replace = skipPush.current; skipPush.current = false;
      if (url.href === window.location.href) return;
      if (replace) window.history.replaceState({}, "", url); else window.history.pushState({}, "", url);
    } catch { /* no-op */ }
  }, [criteria.occasion, criteria.season, criteria.sleepwear_style, criteria.whom]);
  useEffect(() => {
    const onPop = () => { const u = readUrl(); skipPush.current = true; setCriteria((cr) => ({ ...cr, occasion: u.occasion || cr.occasion, season: u.season || "all_seasons", sleepwear_style: u.sleepwear_style || "", whom: u.whom || (audienceOf(cr.whom) !== "all" ? "kids_only" : cr.whom) })); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const resetFilters = () => { setResults(null); setRestoreNote(null); setCriteria((cr) => ({ ...DEFAULT_CRITERIA, child_ids: filterByWhom((fam && fam.children) || [], "kids_only").map((c) => c.id) })); toast.info("Filters reset to the defaults: All the children · Pajamas · All seasons · Any brand · no budget."); };
  const scrollToPanel = () => document.getElementById("family-filter-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });

  const build = async () => {
    if (fam && fam.children && fam.children.length > 0 && criteria.child_ids.length === 0) {
      toast.info("Add at least one child to this shopping session.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...criteria, budget_amount: criteria.budget_amount ? Number(criteria.budget_amount) : null };
      const { data } = user ? await api.post("/match/build", payload) : await api.post("/match/build-guest", { ...payload, guest_children: (guestFamily.children || []) });
      setResults(data);
      if (data.results.length === 0 && data.catalog_count !== 0) toast.info("No complete match covers everyone selected. Adjust the filters or save your search for later.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const buildFilters = () => ({
    whom: criteria.whom,
    child_ids: criteria.child_ids,
    occasion: criteria.occasion,
    season: criteria.season || "all_seasons",
    sleepwear_style: criteria.sleepwear_style || "",
    match_type: criteria.match_type,
    specific_brand: !!criteria.specific_brand,
    brands: criteria.brands || [],
    brand_mode: criteria.brand_mode || "prefer",
    complete_the_look: !!criteria.complete_the_look,
    finishing_pieces: criteria.finishing_pieces || [],
    color_relationship: !!criteria.color_relationship,
    budget_type: criteria.budget_type,
    budget_amount: criteria.budget_amount ? Number(criteria.budget_amount) : null,
    sizes: criteria.sizes || [],
    retailers: criteria.retailers || [],
  });

  const defaultSearchName = () => {
    const occ = (criteria.occasion || "").replace(/_/g, " ");
    const whomLabel = (WHOM.find((w) => w.v === criteria.whom) || {}).label || "children";
    const label = `${occ} · ${whomLabel}`.trim();
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const saveSearch = () => {
    if (!user) {
      const payload = { kind: "match_builder", name: defaultSearchName(), filters: buildFilters() };
      localStorage.setItem("pending_saved_search", JSON.stringify(payload));
      toast.message("Sign in to save this search");
      localStorage.setItem("pending_auth_return", `${location.pathname}${window.location.search}`);
      navigate("/auth");
      return;
    }
    setDur({ mode: "30", endDate: "" });
    setSaveOpen(true);
  };

  // Price watch: a normal saved search pinned to ONE collection, carrying the exact family selection, filters, selected
  // accessories and target budget so the watcher evaluates the same complete total the results showed.
  const watchPayload = (d) => ({ kind: "match_builder", name: `Price watch · ${d.name}`, watch_days: 30, filters: { ...buildFilters(), child_ids: (results?.targets || []).map((t) => t.id), watch_collection_id: d.id, watch_collection_name: d.name, watch_child_ids: (results?.targets || []).map((t) => t.id), watch_accessory_ids: (d.budget_summary?.extras || []).map((e) => e.product_id).filter(Boolean), watch_total_at_save: d.budget_summary?.selected_total ?? null } });
  const saveWatch = async (d) => {
    const payload = watchPayload(d);
    if (!user) {
      localStorage.setItem("pending_saved_search", JSON.stringify(payload));
      localStorage.setItem("pending_auth_return", `${location.pathname}${window.location.search}`);
      toast.message("Sign in to save this price watch");
      navigate("/auth");
      return;
    }
    try {
      const { data } = await api.post("/saved-searches", payload);
      toast[data.duplicate ? "info" : "success"](data.duplicate ? "You're already watching this collection at this budget." : `Watching ${d.name} — we'll ding you in-app when its total is within your budget.`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Couldn't save this watch."); }
  };

  const performSave = async () => {
    const payload = { kind: "match_builder", name: defaultSearchName(), filters: buildFilters() };
    if (dur.mode === "indefinite") payload.indefinite = true;
    else if (dur.mode === "custom") {
      if (!dur.endDate) { toast.error("Choose an end date."); return; }
      payload.end_at = dur.endDate;
    } else payload.watch_days = Number(dur.mode);
    setSaving(true);
    try {
      const { data } = await api.post("/saved-searches", payload);
      if (data.duplicate) toast.info("You already saved this search.");
      else if (payload.indefinite) toast.success("Search saved — matches will show in Ding Me until you stop it.");
      else toast.success(`Search saved — matches will show in Ding Me${data.end_date_display ? " through " + data.end_date_display : ""}.`);
      setSaveOpen(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Couldn't save this search.");
    } finally {
      setSaving(false);
    }
  };

  // Rerun/edit a saved search: land here with its criteria and run the finder.
  // Stored children are reconciled with the CURRENT family: group audiences are recomputed from today's children; a Custom
  // search keeps only children that still exist and is never broadened. Legacy whom values map to "All the children".
  useEffect(() => {
    const f = location.state?.rerunFilters;
    if (f && fam) {
      const kids = fam.children || [];
      const known = new Set(kids.map((c) => c.id));
      const whom = WHOM.some((w) => w.v === f.whom) ? f.whom : "kids_only";
      const stored = Array.isArray(f.child_ids) ? f.child_ids : [];
      const kept = stored.filter((id) => known.has(id));
      const dropped = stored.length - kept.length;
      const child_ids = whom === "pick" ? kept : filterByWhom(kids, whom).map((c) => c.id);
      setRestoreNote(dropped > 0 || (whom === "pick" && kept.length === 0) ? { dropped, custom: whom === "pick", remaining: child_ids.length } : null);
      setCriteria((cr) => ({
        ...cr,
        whom,
        occasion: f.occasion || cr.occasion,
        match_type: f.match_type === "complete_collection" ? "coordinating" : (f.match_type || cr.match_type),
        specific_brand: f.specific_brand != null ? !!f.specific_brand : cr.specific_brand,
        brands: f.brands || cr.brands,
        brand_mode: f.brand_mode || cr.brand_mode,
        complete_the_look: f.complete_the_look != null ? !!f.complete_the_look : cr.complete_the_look,
        finishing_pieces: f.finishing_pieces || cr.finishing_pieces,
        color_relationship: f.color_relationship != null ? !!f.color_relationship : cr.color_relationship,
        budget_type: f.budget_type || cr.budget_type,
        budget_amount: f.budget_amount != null ? String(f.budget_amount) : "",
        child_ids,
      }));
      if (location.state?.runNow && child_ids.length > 0) setPendingRun(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, fam]); // eslint-disable-line react-hooks/exhaustive-deps

  const chooseChildren = () => { setOpenId("whom"); scrollToPanel(); };

  useEffect(() => {
    if (pendingRun) {
      setPendingRun(false);
      build();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRun]);

  const getLinks = async (deal) => {
    try {
      const { data } = await api.post(`/deals/${deal.id}/links`, {});
      if (!data.complete) { toast.error(data.message); return; }
      setLinkModal({ deal, links: data.links });
    } catch (err) {
      if (err.response?.status === 403) { navigate("/upgrade"); return; }
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const trackClick = (link, deal) => {
    api.post("/clicks", { product_id: link.product_id, collection_id: deal.id, retailer: link.retailer }).catch(() => {});
    const dest = (link.affiliate_url && String(link.affiliate_url).startsWith("http")) ? link.affiliate_url : link.url;
    if (dest && String(dest).startsWith("http")) window.open(dest, "_blank", "noopener,noreferrer");
  };

  const isGuest = !user;
  const signIn = () => { localStorage.setItem("pending_auth_return", `${location.pathname}${window.location.search}`); navigate("/auth"); };
  if (!isGuest && family === null && !famLoaded) return <div className="py-16 text-center text-ink-muted">Loading…</div>;
  if (!isGuest && (!family || !family.children || family.children.length === 0)) {
    return (
      <Gate title="Add your children first" eid="deals.gate.nokids" body="Tell us your children's nicknames and sizes (no full names — privacy first) so you can find coordinated sets in the sizes you choose." cta="Create Family Profile" onClick={() => navigate("/family-profile")} />
    );
  }

  const canUnlock = !!user?.is_premium;

  return (
    <div className="space-y-6 fade-up relative">
      <SoftBackdrop />
      <Seo title="Curated Family Sets" description="Browse coordinated family looks for baby, boys and girls — filter by occasion, season and matching style." path="/family-deals" />
      <FindMeTabs />
      <div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-ink flex items-center gap-2"><ShoppingBag className="text-brand" /> <Editable id="deals.title">Curated Family Sets</Editable></h1>
        {isGuest && <p className="text-sm text-ink-soft mt-1" data-testid="guest-intro">Browse as a guest — add your children's age groups below. <button type="button" onClick={signIn} data-testid="family-deals-signin-link" className="text-brand font-semibold underline underline-offset-2 hover:text-brand-hover min-h-[44px]">Sign in to use your saved family</button></p>}
        <p data-testid="results-affiliate-disclosure" className="text-[11px] text-ink-muted mt-1 inline-flex items-center gap-1.5"><ExternalLink size={11} className="text-brand shrink-0" /> {DISCLOSURE}</p>
      </div>

      {catalogEmpty && !demoCount && (
        <div data-testid="prelaunch-collections-banner" className="rounded-3xl border border-blush bg-gradient-to-br from-white via-blush/40 to-white p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="font-heading font-extrabold text-lg text-ink">Our first collections are on the way.</p>
            <p className="text-sm text-ink-soft mt-1">Explore a sample look, or set up your family's sizes. You can still set up and save a family search below — there are no live shopping results yet.</p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <button onClick={() => navigate("/demo-collection")} data-testid="prelaunch-demo-link" className="h-11 px-5 rounded-full bg-brand text-white font-bold text-sm hover:bg-brand-hover transition-all">Explore a sample look</button>
            <button onClick={() => navigate(user ? "/family-profile" : "/auth")} data-testid="prelaunch-sizing-link" className="h-11 px-5 rounded-full border-2 border-brand/40 bg-white text-brand font-bold text-sm hover:bg-blush/50 transition-all">Set up your family's sizes</button>
          </div>
        </div>
      )}

      {isGuest && <GuestChildrenEditor guest={guestFamily} onChange={saveGuestFamily} />}
      {restoreNote && (
        <div data-testid="restore-note" className="rounded-2xl border border-gold/30 bg-gold/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <p className="text-sm font-semibold text-ink">
              {restoreNote.dropped > 0
                ? `${restoreNote.dropped} ${restoreNote.dropped === 1 ? "child" : "children"} from this saved search ${restoreNote.dropped === 1 ? "is" : "are"} no longer in your family.`
                : "This saved search has no children selected."}
            </p>
            <p className="text-xs text-ink-soft mt-1">{restoreNote.custom ? "Custom searches are never widened on their own — pick the children you want from your current profiles." : `Showing your current ${criteria.whom === "boys" ? "boys" : criteria.whom === "girls" ? "girls" : "children"} for this audience; review the selection before saving.`}</p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <button onClick={chooseChildren} data-testid="restore-choose-children" className="h-10 px-5 rounded-full bg-brand text-white font-bold text-sm hover:bg-brand-hover transition-all">Choose children</button>
            <button onClick={() => navigate("/family-profile")} data-testid="restore-add-child" className="h-10 px-4 rounded-full border border-line bg-white text-ink-soft font-semibold text-sm hover:bg-cream transition-all">Add a child</button>
            <button onClick={() => setRestoreNote(null)} data-testid="restore-dismiss" aria-label="Dismiss" className="h-10 w-10 rounded-full text-ink-muted hover:bg-white flex items-center justify-center"><X size={16} /></button>
          </div>
        </div>
      )}
      <FamilyFilterPanel criteria={criteria} setCriteria={setCriteria} family={fam} user={user} selectWhom={selectWhom} audienceZero={audienceZero} loading={loading} saving={saving} onBuild={build} onSave={saveSearch} onReset={resetFilters} kids={filterByWhom(fam.children, criteria.whom)} openId={openId} setOpenId={setOpenId} />
      {audienceZero && (
        <div data-testid="audience-zero-note" className="rounded-2xl border border-gold/30 bg-gold/10 p-4 text-center">
          <p className="text-sm font-semibold text-ink">No eligible {criteria.whom === "boys" ? "boys" : criteria.whom === "girls" ? "girls" : "children"} in your family for this audience yet.</p>
          <p className="text-xs text-ink-soft mt-1">Choose the children for this search from your current profiles, pick a different audience above, or add a child.</p>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            <button onClick={chooseChildren} data-testid="audience-zero-choose-children" className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-brand text-white font-bold text-sm hover:bg-brand-hover transition-all">Choose children</button>
            <button onClick={() => navigate("/family-profile")} data-testid="audience-zero-add-child" className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full border border-line bg-white text-ink-soft font-semibold text-sm hover:bg-cream transition-all">Add a child</button>
          </div>
        </div>
      )}

      {saveOpen && (
        <SaveSearchDialog
          dur={dur}
          setDur={setDur}
          saving={saving}
          onConfirm={performSave}
          onClose={() => setSaveOpen(false)}
        />
      )}

      {results && (results.results.length > 0 || (results.above_budget || []).length > 0) && (
        <div className="space-y-6" data-testid="finder-results">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-heading font-extrabold text-2xl text-ink" data-testid="results-heading">{results.budget ? `${results.results.length} within your budget` : `${results.results.length} matching ${results.results.length === 1 ? "set" : "sets"}`}</h2>
            <AppliedSummary criteria={criteria} onEdit={scrollToPanel} testid="results-applied-filters" />
          </div>
          <p className="text-[11px] text-ink-muted -mt-4" data-testid="price-disclosure">{results.price_disclosure || "Totals exclude tax and shipping."}{results.budget ? ` Selected-family totals count every required piece for each selected child plus the accessories you picked.` : ""}</p>
          {results.budget && results.results.length === 0 && <p className="rounded-2xl border border-line bg-white p-4 text-sm text-ink-soft" data-testid="within-budget-empty">Nothing is within your {results.budget.type === "per_child" ? "per-child" : "family"} budget of ${Number(results.budget.amount).toFixed(0)} right now.</p>}
          {results.results.length > 0 && (
            <section data-testid="within-budget-section" className={results.budget ? "rounded-3xl border-2 border-success/30 bg-success/5 p-4 sm:p-6" : ""}>
              {results.budget && (
                <div className="flex items-center gap-3 mb-5" data-testid="within-budget-heading">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success text-white"><Check size={18} strokeWidth={3} /></span>
                  <div>
                    <h3 className="font-heading font-extrabold text-xl sm:text-2xl text-ink leading-tight">Within your budget</h3>
                    <p className="text-xs sm:text-sm text-success font-bold">{results.results.length} {results.results.length === 1 ? "set fits" : "sets fit"} your {results.budget.type === "per_child" ? "per-child" : "family"} budget</p>
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="within-budget-group">
                {results.results.map((d) => (
                  <div key={d.id} className="flex flex-col gap-2">
                    <DealCard deal={d} canUnlock={canUnlock} fits={fits[d.id]} onGetLinks={getLinks} onPreview={() => navigate("/upgrade")} />
                    {results.budget && d.budget_summary && <BudgetLine bs={d.budget_summary} id={d.id} />}
                  </div>
                ))}
              </div>
            </section>
          )}
          {(results.above_budget || []).length > 0 && (
            <>
              <div role="separator" aria-label="Above your budget starts here" data-testid="budget-divider" className="relative my-12 sm:my-16">
                <div className="h-1 rounded-full bg-brand/60" />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 inline-flex items-center gap-2 rounded-full bg-brand text-white px-4 py-1.5 text-xs sm:text-sm font-extrabold uppercase tracking-wide shadow-sm whitespace-nowrap"><ArrowDown size={14} strokeWidth={3} /> Above your budget</span>
              </div>
              <section data-testid="above-budget-group" className="rounded-3xl border-2 border-brand/40 bg-blush/30 p-4 sm:p-6">
                <div className="flex items-center gap-3" data-testid="above-budget-heading">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand text-white"><TrendingUp size={18} strokeWidth={3} /></span>
                  <div>
                    <h3 className="font-heading font-extrabold text-xl sm:text-2xl text-ink leading-tight">Above your budget <span className="text-ink-muted text-base font-semibold">· {results.above_budget.length}</span></h3>
                    <p className="text-xs sm:text-sm text-brand font-bold">Every other rule met — only the price is over</p>
                  </div>
                </div>
                <p className="text-sm text-ink-soft mt-3">These sets meet every other rule — every selected child covered, style, occasion, season and brands — but cost more than your budget. Save one and we'll ding you in-app when its total comes within your budget.</p>
                <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {results.above_budget.map((d) => (
                    <div key={d.id} className="flex flex-col gap-2" data-testid={`above-budget-card-${d.id}`}>
                      <DealCard deal={d} canUnlock={canUnlock} fits={fits[d.id]} onGetLinks={getLinks} onPreview={() => navigate("/upgrade")} />
                      <BudgetLine bs={d.budget_summary} id={d.id} />
                      <button onClick={() => saveWatch(d)} data-testid={`watch-price-${d.id}`} className="h-11 rounded-full bg-white border-2 border-brand/40 text-brand font-bold text-sm hover:bg-blush/50 transition-all inline-flex items-center justify-center gap-2"><BookmarkPlus size={16} /> Save and ding me when price drops</button>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}
          {results.price_unknown_count > 0 && <p className="text-xs text-ink-muted" data-testid="price-unknown-note">{results.price_unknown_count} matching {results.price_unknown_count === 1 ? "set is" : "sets are"} left out of both groups: a required price isn't confirmed, so we never count it as within budget.</p>}
        </div>
      )}

      {results && results.sleepwear_notes && results.sleepwear_notes.length > 0 && (
        <ul data-testid="sleepwear-notes" className="mb-4 space-y-1.5">
          {results.sleepwear_notes.map((n) => (
            <li key={n.child_id} data-testid={`sleepwear-note-${n.child_id}`} className={`text-sm rounded-2xl px-4 py-2.5 border ${n.satisfiable ? "bg-white border-line text-ink-soft" : "bg-gold/10 border-gold/30 text-ink font-semibold"}`}>
              <span className="font-bold">{n.nickname}</span> · {n.preference_label}{n.message ? ` — ${n.message}` : ""}
            </li>
          ))}
        </ul>
      )}

      {results && results.results.length === 0 && results.catalog_count === 0 && !demoCount && (
        <div data-testid="prelaunch-no-results" className="bg-white rounded-3xl border border-line p-6 text-center">
          <p className="font-heading font-extrabold text-lg text-ink">No live shopping results yet</p>
          <p className="text-sm text-ink-soft mt-1 max-w-md mx-auto">Your filters are set up — nothing is for sale on Siblingo yet. Save this search and matches will appear in Ding Me as our first collections become available.</p>
        </div>
      )}

      {results && results.results.length === 0 && (results.above_budget || []).length === 0 && results.catalog_count !== 0 && (
        <div data-testid="zero-result-state" className="bg-white rounded-3xl border border-line p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-blush text-brand flex items-center justify-center mx-auto mb-3"><ShoppingBag size={26} /></div>
          <p className="font-heading font-extrabold text-lg text-ink" data-testid="zero-result-message">{results.zero_message || "No complete matches are currently available for everyone selected."}</p>
          <p className="text-sm text-ink-soft mt-1 max-w-md mx-auto">We only show sets that cover <b>every</b> child you selected. Your selection is kept — adjust the filters, or save your search for later and we'll show a match in Ding Me (in-app; email digests are coming soon).</p>
        </div>
      )}

      <DemoCollectionsGrid onCount={setDemoCount} />
      {!demoCount && <CollectionsShowcase canUnlock={canUnlock} onData={(d) => setCatalogEmpty(d ? d.length === 0 : null)} suppressEmpty={audienceZero || (results && results.results.length === 0)} onCard={(c) => navigate(`/deal/${c.id}`)} occasion={criteria.occasion} season={criteria.season || "all_seasons"} sleepwear={criteria.occasion === "pajamas" ? criteria.sleepwear_style : ""} audience={audienceOf(criteria.whom)} onEdit={scrollToPanel} onReset={resetFilters} summary={<AppliedSummary criteria={criteria} onEdit={scrollToPanel} testid="collections-applied-filters" />} />}

      {linkModal && (
        <div className="fixed inset-0 z-[60] bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLinkModal(null)}>
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-lift" onClick={(e) => e.stopPropagation()} data-testid="links-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-extrabold text-xl text-ink"><Editable id="deals.modal-title">Shop this collection</Editable></h3>
              <button onClick={() => setLinkModal(null)} data-testid="close-links-modal" className="p-2 rounded-full hover:bg-cream"><X size={20} /></button>
            </div>
            <Editable as="p" id="deals.modal-sub" className="text-sm text-ink-soft mb-4">SIBLINGO doesn't process purchases — you buy directly at each store.</Editable>
            <p data-testid="modal-affiliate-disclosure" className="text-xs text-ink-soft mb-4 -mt-2">{DISCLOSURE}</p>
            <div className="space-y-2 max-h-80 overflow-auto">
              {linkModal.links.map((l) => {
                const dest = (l.affiliate_url && String(l.affiliate_url).startsWith("http")) ? l.affiliate_url : l.url;
                const validUrl = typeof dest === "string" && dest.startsWith("http");
                return validUrl ? (
                <button
                  key={l.product_id}
                  onClick={() => trackClick(l, linkModal.deal)}
                  data-testid={`retailer-link-${l.product_id}`}
                  className="w-full flex items-center justify-between gap-3 p-4 rounded-2xl border border-line hover:border-brand/40 hover:bg-blush transition-all text-left"
                >
                  <div>
                    <p className="font-bold text-ink text-sm">{l.name}</p>
                    <p className="text-xs text-ink-muted">{l.retailer} · ${l.price}</p>
                  </div>
                  <ExternalLink className="text-brand" size={18} />
                </button>
                ) : (
                <div
                  key={l.product_id}
                  data-testid={`retailer-link-unavailable-${l.product_id}`}
                  className="w-full flex items-center justify-between gap-3 p-4 rounded-2xl border border-line bg-cream/60 text-left opacity-70 cursor-not-allowed"
                >
                  <div>
                    <p className="font-bold text-ink text-sm">{l.name}</p>
                    <p className="text-xs text-ink-muted">Link unavailable — not verified yet</p>
                  </div>
                  <X className="text-ink-muted" size={16} />
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AppliedSummary({ criteria, onEdit, testid }) {
  const occ = (OCCASIONS.find((o) => o.v === criteria.occasion) || {}).label || "All occasions";
  const season = criteria.season && criteria.season !== "all_seasons" ? (SEASONS.find((s) => s.v === criteria.season) || {}).label : null;
  const sleep = criteria.occasion === "pajamas" && criteria.sleepwear_style ? (SLEEPWEAR.find((s) => s.v === criteria.sleepwear_style) || {}).label : null;
  const who = (WHOM.find((w) => w.v === criteria.whom) || {}).label;
  return (
    <p className="text-xs text-ink-muted flex flex-wrap items-center gap-x-1.5" data-testid={testid}>
      <span>Showing: <b className="text-ink-soft">{[who, occ, season, sleep].filter(Boolean).join(" · ")}</b></span>
      {onEdit && <button type="button" onClick={onEdit} data-testid={`${testid}-edit`} className="inline-flex items-center gap-1 font-semibold text-brand hover:underline"><SlidersHorizontal size={12} /> Edit filters</button>}
    </p>
  );
}

// Featured Collections — fed by the ONE shared filter panel above (no duplicate controls, no second state).
function CollectionsShowcase({ canUnlock, onCard, suppressEmpty, onData, occasion = "all", season = "all_seasons", sleepwear = "", audience = "all", onEdit, onReset, summary }) {
  const { user } = useAuth();
  const [collections, setCollections] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const sleepwearActive = occasion === "pajamas" ? sleepwear : "";
  const [fits, setFits] = useState({});
  useEffect(() => {
    setLoaded(false);
    onData && onData(null); // loading: parent drops the ItemList until real data arrives
    const q = new URLSearchParams();
    if (occasion && occasion !== "all") q.set("occasion", occasion);
    if (season && season !== "all_seasons") q.set("season", season);
    if (sleepwearActive && sleepwearActive !== "either") q.set("sleepwear_style", sleepwearActive);
    setLoadError(false);
    api.get(`/collections${q.toString() ? `?${q}` : ""}`)
      .then(({ data }) => { setCollections(data || []); onData && onData(data || []); })
      .catch(() => { setCollections([]); setLoadError(true); onData && onData(null); })
      .finally(() => setLoaded(true));
  }, [occasion, season, sleepwearActive, retry]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user) { setFits({}); return; }
    setFits({});
  }, [user]);
  const filtered = collections.filter((c) => {
    if (audience === "boys") return c.whom !== "girls";
    if (audience === "girls") return c.whom !== "boys";
    return true;
  });
  const narrowed = (occasion && occasion !== "all") || (season && season !== "all_seasons") || !!sleepwearActive || audience !== "all";
  const occLabel = (OCCASIONS.find((o) => o.v === occasion) || {}).label || "";
  const seasonLabel = (SEASONS.find((s) => s.v === season) || {}).label || "";
  const sleepLabel = (SLEEPWEAR.find((s) => s.v === sleepwearActive) || {}).label || "";
  const header = (
    <div className="mb-4">
      <h2 className="font-heading font-extrabold text-2xl text-ink flex items-center gap-2"><ShoppingBag className="text-brand" size={22} /> {loaded && collections.length === 0 ? <span data-testid="collections-coming-title">Coming soon: our first collections</span> : <Editable id="deals.collection.title">Featured Collections</Editable>}</h2>
      <p className="text-xs text-ink-muted mt-1" data-testid="collections-subtitle">{collections.length === 0 ? "Nothing is for sale on Siblingo yet — this is where coordinated collections will appear." : "Coordinated sets that cover every child you selected — details reflect our latest content check, and the retailer confirms price and stock at checkout."}</p>
      {summary && <div className="mt-2">{summary}</div>}
    </div>
  );
  if (!loaded) {
    return (
      <div>
        {header}
        <div data-testid="collections-loading" role="status" aria-live="polite" aria-busy="true" aria-label="Loading verified collections" className="space-y-4">
          <p className="text-sm font-semibold text-ink-soft flex items-center gap-2" data-testid="collections-loading-label"><span className="inline-block h-4 w-4 rounded-full border-2 border-brand border-t-transparent animate-spin" aria-hidden="true" /> Loading verified collections…</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" aria-hidden="true">{Array.from({ length: 6 }).map((_, i) => <div key={`sk-${i}`} className="bg-white rounded-3xl border border-line h-80 animate-pulse" />)}</div>
        </div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div>
        {header}
        <div role="alert" data-testid="collections-error" className="bg-white rounded-3xl border border-line p-8 text-center">
          <p className="font-heading font-extrabold text-lg text-ink">We couldn't load collections right now</p>
          <p className="text-sm text-ink-soft mt-1">The catalog service didn't respond (network or server error). Nothing is wrong with your filters — they are kept — please try again.</p>
          <button onClick={() => setRetry((n) => n + 1)} data-testid="collections-retry" className="mt-4 h-11 px-5 rounded-full bg-brand text-white font-bold text-sm hover:bg-brand-hover transition-all">Retry</button>
        </div>
      </div>
    );
  }
  if (filtered.length === 0 && suppressEmpty && !narrowed) return null;
  if (collections.length === 0) {
    return <div>{header}<LaunchState testid="collections-launch-state" /></div>;
  }
  if (filtered.length === 0) {
    return (
      <div>
        {header}
        <div data-testid="collections-empty" className="bg-white rounded-3xl border border-line p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-blush text-brand flex items-center justify-center mx-auto mb-3"><ShoppingBag size={26} /></div>
          <p className="font-heading font-extrabold text-lg text-ink">
            {narrowed ? `No verified ${occLabel || "collections"}${sleepLabel ? ` · ${sleepLabel}` : ""}${seasonLabel ? ` · ${seasonLabel}` : ""}${audience !== "all" ? ` · ${audience === "boys" ? "Boys" : "Girls"}` : ""} collections yet` : "New verified collections are coming soon"}
          </p>
          <p className="text-sm text-ink-soft mt-1 max-w-md mx-auto">We only publish complete, price- and image-verified coordinated sets.{" "}
            {narrowed
              ? (sleepwearActive === "nightgown" ? "We never relabel a pajama set or dress as a nightgown — nightgowns appear here only once a retailer-classified nightgown is verified." : "We never fill an occasion with unrelated items — try another occasion or season.")
              : user ? "Use the finder above to search, and save your search so we can alert you when we find a matching collection." : "Add your children above to search; sign in to save the search so we can alert you when we find a matching collection."}
          </p>
          {narrowed && (
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {onEdit && <button onClick={onEdit} data-testid="collections-edit-filters" className="h-11 px-5 rounded-full border border-brand/40 text-brand bg-white font-semibold text-sm hover:bg-blush/50 transition-colors">Edit filters</button>}
              {onReset && <button onClick={onReset} data-testid="public-filters-reset" className="h-11 px-5 rounded-full bg-brand text-white font-bold text-sm hover:bg-brand-hover transition-all">Clear filters</button>}
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div data-testid="collections-showcase">
      {header}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((c) => <DealCard key={c.id} deal={c} canUnlock={canUnlock} fits={fits[c.id]} />)}
      </div>
    </div>
  );
}

const GUEST_KEY = "siblingo.guest_family"; // guest-only, never merged into a signed-in family
const AGE_GROUPS = [["baby_girl", "Baby girl (0–24 M)"], ["baby_boy", "Baby boy (0–24 M)"], ["girl", "Girl (2–12)"], ["boy", "Boy (2–12)"], ["teen_girl", "Teen girl"], ["teen_boy", "Teen boy"]];
function readGuestFamily() {
  try { const g = JSON.parse(localStorage.getItem(GUEST_KEY) || "null"); if (g && Array.isArray(g.children)) return g; } catch { /* ignore */ }
  return { id: "guest", children: [] };
}
function writeGuestFamily(g) { try { localStorage.setItem(GUEST_KEY, JSON.stringify(g)); } catch { /* ignore */ } }

// Soft flowing blush / ivory backdrop (pure CSS gradients — no imagery)
function SoftBackdrop() {
  return <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 bg-[#FFFBF7]" style={{ backgroundImage: "radial-gradient(60% 45% at 85% 0%, rgba(255,192,203,0.55), transparent 70%), radial-gradient(55% 40% at 0% 35%, rgba(255,228,232,0.9), transparent 70%), radial-gradient(50% 45% at 100% 80%, rgba(255,209,220,0.6), transparent 70%), radial-gradient(45% 35% at 20% 100%, rgba(255,246,238,1), transparent 70%), linear-gradient(180deg, #FFF7F9 0%, #FFFBF7 50%, #FFF3F6 100%)" }} data-testid="soft-backdrop" />;
}

function GuestChildrenEditor({ guest, onChange }) {
  const [type, setType] = useState("girl");
  const [size, setSize] = useState("");
  const kids = guest.children || [];
  const add = () => { const label = AGE_GROUPS.find((a) => a[0] === type)[1].split(" (")[0]; onChange({ ...guest, children: [...kids, { id: `g-${Date.now()}`, nickname: `${label} ${kids.filter((k) => k.type === type).length + 1}`, type, brand_sizes: size.trim() ? [{ brand: "", clothing_type: "", fit: "regular", size: size.trim(), kind: "wears" }] : [], size_note: size.trim() }] }); setSize(""); };
  const remove = (id) => onChange({ ...guest, children: kids.filter((k) => k.id !== id) });
  return (
    <section className="bg-white/90 backdrop-blur rounded-3xl border border-brand/15 shadow-soft p-4 sm:p-5 space-y-3" data-testid="guest-children">
      <div><h2 className="font-heading font-extrabold text-base md:text-lg text-ink">Who are you shopping for?</h2><p className="text-xs text-ink-muted">Kept only on this device while you browse — not saved to an account.</p></div>
      <ul className="flex flex-wrap gap-2" aria-label="Guest children">
        {kids.map((k) => <li key={k.id} data-testid={`guest-child-${k.id}`} className="inline-flex items-center gap-2 rounded-full bg-blush/60 border border-brand/20 pl-3 pr-1 py-1 text-sm text-ink"><span>{k.nickname}{k.size_note ? ` · size ${k.size_note}` : ""}</span><button type="button" aria-label={`Remove ${k.nickname}`} onClick={() => remove(k.id)} className="h-8 w-8 rounded-full hover:bg-white flex items-center justify-center text-ink-soft" data-testid={`guest-child-remove-${k.id}`}>×</button></li>)}
        {kids.length === 0 && <li className="text-sm text-ink-soft" data-testid="guest-children-empty">Add at least one child to match sets to your family.</li>}
      </ul>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
        <label className="block"><span className="text-xs font-semibold text-ink-soft">Age group</span><select data-testid="guest-age-group" value={type} onChange={(e) => setType(e.target.value)} className="mt-1 h-11 w-full rounded-2xl border-2 border-line bg-white px-3 text-sm focus:border-brand outline-none">{AGE_GROUPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
        <label className="block"><span className="text-xs font-semibold text-ink-soft">Usual size (optional)</span><input data-testid="guest-size" value={size} onChange={(e) => setSize(e.target.value)} placeholder="e.g. 6 or 12 M" className="mt-1 h-11 w-full rounded-2xl border-2 border-line bg-white px-3 text-sm focus:border-brand outline-none" /></label>
        <button type="button" onClick={add} data-testid="guest-add-child" className="self-end h-11 px-5 rounded-full bg-brand text-white font-bold text-sm hover:bg-brand-hover">Add child</button>
      </div>
    </section>
  );
}

function Gate({ title, body, cta, onClick, eid }) {
  return (
    <div className="max-w-lg mx-auto text-center py-16 fade-up">
      <div className="w-16 h-16 rounded-3xl bg-brand-light flex items-center justify-center mx-auto mb-5"><ShoppingBag className="text-brand" size={30} /></div>
      <Editable as="h1" id={`${eid}.title`} className="font-heading font-black text-3xl text-ink">{title}</Editable>
      <Editable as="p" id={`${eid}.body`} className="text-ink-soft mt-3">{body}</Editable>
      <button onClick={onClick} data-testid="gate-cta-btn" className="mt-6 h-14 px-8 rounded-full bg-brand text-white font-bold text-lg hover:bg-brand-hover active:scale-95 transition-all shadow-md"><Editable id={`${eid}.cta`}>{cta}</Editable></button>
    </div>
  );
}

const DUR_OPTIONS = [
  { v: "7", label: "7 days" },
  { v: "30", label: "30 days", rec: true },
  { v: "60", label: "60 days" },
  { v: "90", label: "90 days" },
  { v: "custom", label: "Choose an end date" },
  { v: "indefinite", label: "Until I stop it" },
];

function SaveSearchDialog({ dur, setDur, saving, onConfirm, onClose }) {
  const fmt = (d) => d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const previewEnd = () => {
    if (dur.mode === "indefinite") return null;
    if (dur.mode === "custom") return dur.endDate ? fmt(new Date(dur.endDate + "T00:00:00")) : null;
    const d = new Date(); d.setDate(d.getDate() + Number(dur.mode));
    return fmt(d);
  };
  const end = previewEnd();
  const todayIso = new Date().toISOString().slice(0, 10);
  return (
    <div className="fixed inset-0 z-[95] bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="How long should we watch this search?"
        data-testid="save-search-dialog"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
        className="bg-white rounded-3xl shadow-lift w-full max-w-md max-h-[92vh] overflow-y-auto p-6 fade-up"
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-black text-xl text-ink">How long should we watch this search?</h3>
          <button onClick={onClose} data-testid="save-search-dialog-close" aria-label="Close" className="p-2 rounded-full text-ink-muted hover:bg-cream"><X size={20} /></button>
        </div>
        <p className="text-sm text-ink-soft mb-4" data-testid="save-search-channel-note">Matches will appear in Ding Me (in-app) until this search ends. Email and text alerts aren't live yet — nothing will be sent.</p>

        <div role="radiogroup" aria-label="Watch duration" className="space-y-2">
          {DUR_OPTIONS.map((o) => (
            <label
              key={o.v}
              data-testid={`dur-option-${o.v}`}
              className={`flex items-center gap-3 p-3 rounded-2xl border-2 cursor-pointer transition-all ${dur.mode === o.v ? "border-brand bg-blush/50" : "border-line hover:border-brand/30"}`}
            >
              <input
                type="radio"
                name="watch-duration"
                checked={dur.mode === o.v}
                onChange={() => setDur({ ...dur, mode: o.v })}
                className="accent-brand"
              />
              <span className="text-sm font-semibold text-ink flex-1">
                {o.label}{o.rec && <span className="ml-2 text-xs font-bold text-brand">Recommended</span>}
              </span>
            </label>
          ))}
        </div>

        {dur.mode === "custom" && (
          <div className="mt-3">
            <label htmlFor="custom-end-date" className="text-xs font-bold uppercase tracking-wider text-ink-muted">End date</label>
            <input
              id="custom-end-date"
              type="date"
              min={todayIso}
              value={dur.endDate}
              onChange={(e) => setDur({ ...dur, endDate: e.target.value })}
              data-testid="dur-custom-date"
              className="mt-1 w-full h-12 px-4 rounded-2xl border-2 border-line bg-cream focus:border-brand outline-none"
            />
          </div>
        )}

        <div data-testid="dur-preview" className="mt-4 rounded-2xl bg-cream border border-line p-3 text-sm">
          {dur.mode === "indefinite" ? (
            <p className="text-ink font-semibold">We'll watch this search <span className="text-brand">until you stop it</span>. Alerts keep coming until you pause or delete it.</p>
          ) : end ? (
            <p className="text-ink font-semibold">We'll watch this search through <span className="text-brand">{end}</span>.</p>
          ) : (
            <p className="text-ink-muted">Choose an end date above to see when we'll stop watching.</p>
          )}
        </div>

        <div className="flex gap-2 pt-4">
          <button onClick={onClose} data-testid="save-search-cancel" className="h-12 px-4 rounded-full border-2 border-line text-ink-soft font-bold hover:border-brand/30 transition-all">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={saving || (dur.mode === "custom" && !dur.endDate)}
            data-testid="save-search-confirm"
            className="flex-1 h-12 rounded-full bg-brand text-white font-bold hover:bg-brand-hover transition-all flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <BookmarkPlus size={18} /> {saving ? "Saving…" : "Save search"}
          </button>
        </div>
      </div>
    </div>
  );
}
